import InboxIcon from '@mui/icons-material/InboxOutlined';
import { Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';
import type { ReactNode } from 'react';

/**
 * What a list says when it has nothing in it.
 *
 * It used to be the words "Kayıt yok" in the middle of an otherwise blank
 * table, which reads as a table that failed rather than as a table that is
 * empty — a distinction that matters, because the first is worth telling
 * somebody about and the second is not.
 *
 * The icon is drawn large and faint rather than at text size: it should fill
 * the hole the missing rows left, not sit in it like one more row.
 */
export function EmptyState({
  icon: Icon = InboxIcon,
  title,
  body,
  action,
}: {
  icon?: SvgIconComponent;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Stack spacing={1.5} sx={{ alignItems: 'center', py: 6, px: 3, textAlign: 'center' }}>
      <Stack
        aria-hidden
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          backgroundColor: alpha(theme.palette.text.secondary, 0.08),
        }}
      >
        <Icon sx={{ fontSize: 34 }} />
      </Stack>

      <Typography variant="h3" sx={{ color: 'text.primary' }}>
        {title}
      </Typography>

      {body ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>
          {body}
        </Typography>
      ) : null}

      {action}
    </Stack>
  );
}
